import { useEffect, lazy, Suspense } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import RelarkLandingPage from "./RelarkLandingPage";
import { useBrowserLanguageRedirect } from "@/hooks/useBrowserLanguageRedirect";
import { getDomainConfig } from "@/lib/domain-config";
import { useGeoLocale } from "@/hooks/useGeoLocale";
import LocaleBanner from "@/components/LocaleBanner";

// Lazy-load Trust Center for the relark.eu route — it's a heavy page and
// only relevant for that single hostname.
const TrustCenter = lazy(() => import("./TrustCenter"));

export default function Index() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const domain = getDomainConfig();

  // Auto-redirect first-time visitors to their browser-language variant
  // (only acts on "/", skips bots, respects manual language choices).
  useBrowserLanguageRedirect();

  // Prioritet 5: TLD → locale → browser fallback; respects user pref;
  // emits geo_locale_auto_set analytics. Replaces the previous one-shot
  // ccTLD sync with a single source of truth.
  useGeoLocale();

  // Render the landing page immediately for everyone — including visitors
  // whose auth state is still loading. Only redirect AFTER we've confirmed
  // there's an active session. This removes the blocking "Loading..." flash
  // and lets the hero paint before Supabase responds.
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  // relark.eu → render Trust Center as the homepage instead of the
  // marketing landing. The domain is positioned as the EU-compliance
  // "official" surface for DPOs, IT teams and public sector buyers.
  if (domain.role === "trust") {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <TrustCenter />
      </Suspense>
    );
  }

  return (
    <>
      <LocaleBanner />
      <RelarkLandingPage />
    </>
  );
}
